import { FC } from 'react';
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import { SERIES_CUSTO, type PontoCusto } from '@/utils/equiplexDre';
import { fmtPct, mesCurto } from '@/utils/painelFormat';
import { useMostrarValores } from '@/hooks/useMostrarValores';
import { mostrarIndices, ultimoPonto, valoresDe } from '@/utils/rotulosGrafico';
import BotaoValores from '../BotaoValores';
import { ALTURA_GRAFICO, corSerie, tickEixo, type CoresPainel } from '../chartTheme';
import Legenda from '../Legenda';
import PainelCard from '../PainelCard';
import { rotuloDoPonto } from '../rotuloBarra';
import TooltipBox from '../TooltipBox';

interface Props {
  serie: PontoCusto[];
  cores: CoresPainel;
}

/**
 * Quanto da receita líquida cada custo e despesa consome, mês a mês (o gráfico do Manus). Cada
 * linha mostra o % do último mês, à direita do ponto (as despesas ficam perto umas das outras);
 * "Mostrar valores" liga o de todos os meses.
 */
const CustosPctChart: FC<Props> = ({ serie, cores }) => {
  const pontos = serie.map((p) => ({ ...p, rotulo: mesCurto(String(p.label)) }));
  const cor = (i: number) => corSerie(i, cores.modo);
  const [todos, alternarTodos] = useMostrarValores();

  const conteudoTooltip = ({ active, payload }: TooltipContentProps) => {
    const p = active && payload?.[0] ? (payload[0].payload as PontoCusto) : null;
    if (!p) return null;
    const linhas = SERIES_CUSTO.map((s, i) => ({ rotulo: s.rotulo, valor: fmtPct((p[s.key] as number | null) ?? null), cor: cor(i) }));
    return <TooltipBox titulo={String(p.label)} linhas={linhas} />;
  };

  return (
    <PainelCard
      testId="dre-chart-custos"
      titulo="Custos e despesas em % da receita líquida"
      subtitulo="Quanto de cada R$ 100 de receita líquida cada linha consome."
      acoes={<BotaoValores ativo={todos} onAlternar={alternarTodos} testId="dre-chart-custos-valores" />}
    >
      <Legenda itens={SERIES_CUSTO.map((s, i) => ({ rotulo: s.rotulo, cor: cor(i) }))} />
      <div style={{ width: '100%', height: ALTURA_GRAFICO }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={pontos} margin={{ top: 24, right: 56, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={cores.grade} />
            <XAxis dataKey="rotulo" tick={tickEixo(cores)} axisLine={{ stroke: cores.eixo }} tickLine={false} interval={0} />
            <YAxis tickFormatter={(v: number) => `${v}%`} tick={tickEixo(cores)} axisLine={false} tickLine={false} width={56} />
            <Tooltip content={conteudoTooltip} />
            {SERIES_CUSTO.map((s, i) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.rotulo}
                stroke={cor(i)}
                strokeWidth={2}
                dot={{ r: 4, strokeWidth: 2, stroke: cores.modo === 'light' ? '#FFFFFF' : '#002538', fill: cor(i) }}
                connectNulls
                isAnimationActive={false}
              >
                <LabelList
                  dataKey={s.key}
                  content={rotuloDoPonto(fmtPct, cores.texto, mostrarIndices(ultimoPonto(valoresDe(pontos, s.key)), todos), {
                    aDireitaNo: pontos.length - 1,
                  })}
                />
              </Line>
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </PainelCard>
  );
};

export default CustosPctChart;
